// Angular Files
import { Inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';

// Other External Files
import { firstValueFrom } from 'rxjs';

// Payment Integration Files
// For some reason if the PaymentProcessorService was included from just base, the unit tests would fail
import { PaymentProcessorService } from 'apps/public-portal/src/app/payment-integrations/base/interfaces';

// Teller Online Files
import { CartService, InboundRedirectService } from 'apps/public-portal/src/app/core/services';
import { 
    RedirectVerifyPaymentStatusRequestDto, 
    RedirectVerifyPaymentStatusResponseDto, 
    SharedPaymentIntegrationApiClient, 
    StartPaymentResponseDto 
} from 'apps/public-portal/src/app/core/api/PaymentIntegrationApiClients';
import { RedirectPaymentProcessorResponse } from 'apps/public-portal/src/app/payment-integrations/base/models';
import { TellerOnlineAppService } from 'teller-online-libraries/core';

@Injectable({
    providedIn: 'root'
})
export class GovolutionService extends PaymentProcessorService {
    private applicationId: string;
    private messageVersion: string;

    //#region PaymentProcessorService fields
    public static override redirect = true;
    //#endregion

    constructor(
        @Inject(DOCUMENT) private document: Document,
        private appService: TellerOnlineAppService,
        private cartService: CartService,
        private inboundRedirectService: InboundRedirectService,
        public sharedPaymentApi: SharedPaymentIntegrationApiClient
    ) {
        super(sharedPaymentApi);
    }

    //#region PaymentProcessorService
    public override async checkout(cartGuid: string) {
        let startPaymentResponse: StartPaymentResponseDto;
        try {
            await this.cartService.openCart();
            startPaymentResponse = await firstValueFrom(this.sharedPaymentApi.startPayment(cartGuid, undefined, this.inboundRedirectService.redirectSourceId));
        } catch (e) {
            this._errorEvent.next('Failed to initialize payment and generate redirect url');
            return
        }

        try {
            // Govolution requires redirect via POST request (form submission)
            const form = this.document.createElement("form");
            form.method = "POST";
            form.action = startPaymentResponse.redirectUrl;
            this.addFormInput(form, "application_id", this.applicationId);
            this.addFormInput(form, "message_version", this.messageVersion);
            this.addFormInput(form, "remittance_id", startPaymentResponse.paymentIdentifier);
            this.document.body.append(form);
            form.submit();
        } catch (e) {
            this._errorEvent.next('Failed to redirect')
        }
    }

    public override async handlePostbackResponse(params) {
        const verifyPaymentStatusRequest = new RedirectVerifyPaymentStatusRequestDto();
        const paymentIdentifier = params.paymentIdentifier;
        verifyPaymentStatusRequest.paymentIdentifier = paymentIdentifier;
        verifyPaymentStatusRequest.paymentStatus = params.result;

        let response: RedirectVerifyPaymentStatusResponseDto;

        if (paymentIdentifier) {
            response = await firstValueFrom(this.sharedPaymentApi.verifyPaymentStatus(verifyPaymentStatusRequest));
        }

        let state: { cartError?: string, emailAddress?: string } = {};
        let redirectUrl = "/";
        let success = response?.responseSuccess;

        if (success) {
            if (this.cartService.cartGuid) {
                redirectUrl = "/checkout/success/" + this.cartService.cartGuid;
                state.emailAddress = response.emailAddress;
            } else {
                state.cartError = "Payment was successful, but the cart could not be retrieved. Check your email for the receipt.";
            }
        } else {
            // Generic message default since this state should not be reachable without setting the URL manually 
            state.cartError = response?.responseMessage ?? "An unknown error occurred while processing the payment.";
        }

        this.appService.consoleLog(this, state);

        return new RedirectPaymentProcessorResponse({
            paymentIdentifier: paymentIdentifier,
            cartGuid: this.cartService.cartGuid,
            status: success ? "success" : "cancel",
            redirectUrl,
            navigationExtras: { state }
        });
    }
    //#endregion

    private addFormInput(form: HTMLFormElement, name: string, value: string) {
        const input = this.document.createElement("input");
        input.type = "hidden";
        input.name = name;
        input.value = value;
        form.append(input);
    }
}
